import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateRuleBody,
  type RuleListParams,
  type TestRuleBody,
  type UpdateRuleBody,
  adminClient,
} from '../lib/admin-client.js';

export const RULE_KEYS = {
  all: (tenantId: string, roleId: string) => ['admin-role-rules', tenantId, roleId] as const,
  list: (tenantId: string, roleId: string, params: RuleListParams) =>
    ['admin-role-rules', tenantId, roleId, 'list', params] as const,
};

export function useRoleRules(tenantId: string, roleId: string, params: RuleListParams = {}) {
  return useQuery({
    queryKey: RULE_KEYS.list(tenantId, roleId, params),
    queryFn: () => adminClient.listRoleRules(tenantId, roleId, params),
    enabled: tenantId.length > 0 && roleId.length > 0,
    staleTime: 30_000,
  });
}

export function useCreateRoleRule(tenantId: string, roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRuleBody) => adminClient.createRoleRule(tenantId, roleId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RULE_KEYS.all(tenantId, roleId) });
    },
  });
}

export function useUpdateRoleRule(tenantId: string, roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, body }: { ruleId: string; body: UpdateRuleBody }) =>
      adminClient.updateRoleRule(tenantId, roleId, ruleId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RULE_KEYS.all(tenantId, roleId) });
    },
  });
}

export function useDeleteRoleRule(tenantId: string, roleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => adminClient.deleteRoleRule(tenantId, roleId, ruleId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RULE_KEYS.all(tenantId, roleId) });
    },
  });
}

export function useTestRoleRule(tenantId: string) {
  return useMutation({
    mutationFn: (body: TestRuleBody) => adminClient.testRoleRule(tenantId, body),
  });
}

export function useApplyRoleRule(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => adminClient.applyRoleRule(tenantId, ruleId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-role-members', tenantId] });
    },
  });
}
